// src/components/achievements/JourneyModal.tsx
// 旅程視覺化 Modal - 圓點時間軸設計
//
// 設計理念：
// - 將目標的完成過程視覺化為一段「旅程」
// - 圓點時間軸：每週一個圓點，大小代表 Ripples 數量
// - 讓用戶感受努力的軌跡和成就的價值

import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { format, differenceInWeeks } from 'date-fns';
import { Objective } from '../../features/goal/types/objective';
import { getCategoryConfig } from '../../features/goal/utils/objectiveUtils';
import { toDateObject } from '../../utils/dateUtils';
import { CERAMIC_VARIANTS, DEFAULT_VARIANT } from '../../features/goal/constants/categoryStyles';

interface JourneyModalProps {
  objective: Objective;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * JourneyModal - 旅程視覺化 Modal
 *
 * 視覺結構：
 * ┌──────────────────────────────────┐
 * │  [X]         Journey             │  ← Header
 * ├──────────────────────────────────┤
 * │      [Large Icon + Glow]         │  ← 64px 圖標
 * │      「目標標題」                 │
 * ├──────────────────────────────────┤
 * │  Started        →        Completed│
 * │  Oct 1 ──────────────── Dec 15   │  ← 時間軸線
 * ├──────────────────────────────────┤
 * │  ○──●──●──○──●──●──○──●──●──○──● │  ← 圓點時間軸
 * │  W1  W2  W3  W4  W5  W6 ... W12  │
 * ├──────────────────────────────────┤
 * │         156 Ripples              │  ← 總計卡片
 * └──────────────────────────────────┘
 */

const JourneyModal: React.FC<JourneyModalProps> = ({
  objective,
  isOpen,
  onClose,
}) => {
  // ESC 鍵關閉處理
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // 防止背景滾動
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // 如果未開啟則不渲染
  if (!isOpen) return null;

  const categoryConfig = getCategoryConfig(objective.category);
  const CategoryIcon = categoryConfig.icon;

  const categoryKey = objective.category || 'growth';
  const variant = CERAMIC_VARIANTS[categoryKey] || DEFAULT_VARIANT;

  // 日期計算
  const startDate = toDateObject(objective.startDate);
  const completedDate = objective.completedAt
    ? toDateObject(objective.completedAt)
    : new Date();

  const durationWeeks = Math.max(1, differenceInWeeks(completedDate, startDate));

  // 週間 Ripples 資料
  const weeklyRipples = objective.weeklyRipples || [];
  const maxRipples = Math.max(...weeklyRipples, 1);

  // 計算圓點大小（8px - 24px）
  const getDotSize = (ripples: number): number => {
    if (ripples === 0) return 8;
    const ratio = ripples / maxRipples;
    return Math.round(8 + ratio * 16); // 8px 到 24px
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-modal bg-black/40 backdrop-blur-sm
                 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="journey-modal-title"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh]
                   overflow-y-auto shadow-xl animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm
                        flex items-center justify-between p-4 border-b border-gray-100
                        rounded-t-2xl z-10">
          <button
            onClick={onClose}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-gray-500" />
          </button>
          <span className="font-semibold text-gray-800">Journey</span>
          <div className="w-8" />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Hero Section - 大圖標 + 標題 */}
          <div className="text-center flex flex-col items-center">
            {/* Consistent with MementoCard visuals */}
            <div
              className={`inline-flex w-28 h-28 rounded-full items-center justify-center mb-4 relative overflow-hidden border-4 ${variant.bgClass} ${variant.borderClass.replace('border-', 'border-opacity-30 border-')} ${variant.borderClass}`}
              style={{
                boxShadow: 'inset -3px -3px 6px rgba(255,255,255,0.8), inset 1px 1px 2px rgba(0,0,0,0.05), 2px 2px 4px rgba(0,0,0,0.1), -1px -1px 2px rgba(255,255,255,0.5)',
              }}
            >
              {/* Icon Layer */}
              <CategoryIcon
                size={60}
                className={`${variant.color} ${variant.shadow} relative z-10`}
                strokeWidth={1.5}
              />
            </div>
            <h2
              id="journey-modal-title"
              className="text-xl font-bold text-gray-900 leading-snug"
            >
              {objective.title}
            </h2>
          </div>

          {/* Timeline Section - 起點到終點 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between items-center">
              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                  Started
                </p>
                <p className="font-medium text-gray-800">
                  {format(startDate, 'MMM d')}
                </p>
              </div>

              {/* 連接線 */}
              <div className="flex-1 mx-4 h-0.5 bg-gradient-to-r from-sky-300 to-pink-300 rounded-full" />

              <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                  Completed
                </p>
                <p className="font-medium text-gray-800">
                  {format(completedDate, 'MMM d')}
                </p>
              </div>
            </div>

            <p className="text-center text-sm text-gray-500 mt-3">
              {durationWeeks} week{durationWeeks !== 1 ? 's' : ''} of dedication
            </p>
          </div>

          {/* Weekly Progress - 圓點時間軸 */}
          {weeklyRipples.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-4">
                Weekly Progress
              </h3>

              {/* 圓點時間軸 */}
              <div className="relative">
                {/* 連接線背景 */}
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-200 to-pink-200 -translate-y-1/2" />

                {/* 圓點容器 */}
                <div className="relative flex justify-between items-center py-2">
                  {weeklyRipples.map((ripples, index) => {
                    const size = getDotSize(ripples);
                    const isEmpty = ripples === 0;

                    return (
                      <div
                        key={index}
                        className="flex flex-col items-center"
                        title={`Week ${index + 1}: ${ripples} Ripples`}
                      >
                        {/* 圓點 */}
                        <div
                          className={`rounded-full transition-all duration-200 ${isEmpty
                            ? 'border-2 border-sky-200 bg-white'
                            : 'bg-gradient-to-br from-sky-500 to-pink-400 shadow-sm'
                            }`}
                          style={{
                            width: `${size}px`,
                            height: `${size}px`,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* 週數標籤（只顯示首尾） */}
                <div className="flex justify-between text-xs text-gray-400 mt-2">
                  <span>W1</span>
                  <span>W{weeklyRipples.length}</span>
                </div>
              </div>
            </div>
          )}

          {/* Total Stats - 總計卡片 */}
          <div className="text-center p-5 bg-gradient-to-r from-sky-100 to-pink-100 rounded-xl">
            <p className="text-4xl font-bold text-sky-600 mb-1">
              {objective.totalRipples}
            </p>
            <p className="text-sm text-gray-600">
              Total Ripples Earned
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm
                        p-4 border-t border-gray-100 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full btn-secondary py-3"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default JourneyModal;

